import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import {
  EMCType,
  EmcOptions as SharedEmcOptions,
  EmcSearchOutput,
  EntityModelCustomizationService as SharedEmcService,
  PermissionsService,
} from 'shared-lib';
import { CustomerService } from '../../../services/customer.service';
import { EntityModelCustomizationService } from '../../../services/entity-model-customization.service';
import { TariffService } from '../../../services/tariff.service';
import { AuthService } from '../../../services/auth.service';
import { ChecklistItem } from '../../../models/checklist-item.model';
import { Tariff } from '../../../models/tariff.model';
import {
  EmcOptions,
  Purpose,
  SectionDefinitionEntityType,
  TableListPage,
} from '../../../models/dynamic-buildings.model';
import {
  CUSTOMER_USE_OF_PREMISES,
  CUSTOMER_VALIDATION_STATUSES,
  DynamicCustomerFilter,
} from '../../../models/dynamic-customers.model';

@Component({
  selector: 'app-dynamic-customers',
  templateUrl: './dynamic-customers.component.html',
  styleUrls: ['../../dynamic-buildings/dynamic-buildings.component.scss'],
})
export class DynamicCustomersComponent implements OnInit, OnDestroy {
  filter: DynamicCustomerFilter = new DynamicCustomerFilter();
  cardsFilter: DynamicCustomerFilter = new DynamicCustomerFilter();

  cardEmc: SharedEmcOptions[] = [];
  listPageEmc: EmcOptions[] = [];
  filterEmc: SharedEmcOptions[] = [];

  entityType: EMCType = EMCType.Customer;

  tableListPage: TableListPage = new TableListPage();
  visibleColumns: { label: string; index: number }[] = [];

  pageSize: number = 10;
  currentPage: number = 1;
  count: number = 0;

  checklistItems: ChecklistItem[] = [];
  isFilterWindowOpened: boolean = false;
  utilityId: number = 0;
  hasCimDataIntegration: boolean = false;
  showDownload: boolean = false;

  tariffs: Tariff[] = [];
  sltOptions: { key: number; value: string }[] = [];

  private ngUnsubscribe = new Subject<void>();
  private storageKey = '';

  constructor(
    private customerService: CustomerService,
    private emcService: EntityModelCustomizationService,
    private sharedEmcService: SharedEmcService,
    private tariffService: TariffService,
    private toastr: ToastrService,
    private router: Router,
    public permissionsService: PermissionsService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.utilityId = parseInt(localStorage.getItem('utilityId') || '0');
    this.storageKey = `filterDynamicCustomers|${this.utilityId}`;
    this.hasCimDataIntegration = this.authService.hasCimDataIntegration;

    const cachedFilters = localStorage.getItem(this.storageKey);
    if (cachedFilters) {
      this.filter = JSON.parse(cachedFilters);
      this.currentPage = this.filter.pageInfo.page ?? 1;
      localStorage.removeItem(this.storageKey);
    }
    this.filter.utilityId = this.utilityId;
    this.filter.filterParams.showAllNewEntries = !this.permissionsService.isCustomerNewEntryBreakdownAllowed;
    this.cardsFilter = this.buildRequest();

    this.loadEmcConfig();
    this.loadFilterLookups();
    this.loadList();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private loadEmcConfig() {
    const entity = SectionDefinitionEntityType.Customer;
    this.emcService.getSelectedEmcOptions(entity, Purpose.Card, this.utilityId).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (r: any) => { this.cardEmc = r.body?.data ?? []; },
    });
    this.emcService.getSelectedEmcOptions(entity, Purpose.ListPage, this.utilityId).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (r: any) => {
        this.listPageEmc = (r.body?.data ?? []).sort((a: EmcOptions, b: EmcOptions) => a.displayOrder - b.displayOrder);
        this.buildVisibleColumns();
      },
    });

    this.sharedEmcService.getSelectedEmcOptionsForFilter(entity, Purpose.Filter).pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (r: any) => {
        this.filterEmc = r.body?.data ?? [];
        this.createFilterCards();
      },
    });
  }

  private loadFilterLookups() {
    this.tariffService.getAllForSelect().pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (r: any) => {
        this.tariffs = r.body?.data ?? [];
        this.createFilterCards();
      },
    });
    this.customerService.getSltStatusesForOptions().pipe(takeUntil(this.ngUnsubscribe)).subscribe({
      next: (r: any) => {
        this.sltOptions = r.body?.data ?? [];
        this.createFilterCards();
      },
    });
  }

  loadList() {
    this.filter.pageInfo.page = this.currentPage;
    this.filter.pageInfo.pageSize = this.pageSize;

    this.customerService.getAllDynamic(this.buildRequest()).subscribe({
      next: (response) => {
        const data = response.body;
        if (data?.status === 200 || data?.status === 'OK') {
          this.tableListPage = data.data ?? new TableListPage();
          this.count = this.tableListPage.count ?? 0;
        } else {
          this.toastr.error(data?.message);
        }
      },
      error: () => this.toastr.error('Error occurred'),
    });
  }


  private buildRequest(): DynamicCustomerFilter {
    const request: DynamicCustomerFilter = JSON.parse(JSON.stringify(this.filter));
    if (request.filterParams.dateTo) {
      request.filterParams.dateTo = `${request.filterParams.dateTo}T23:59:59`;
    }
    return request;
  }

  pageChange(page: number) {
    this.currentPage = page;
    this.loadList();
  }

  onSearchOutput(obj: EmcSearchOutput) {
    this.filter.searchObject = {
      attributeDefinitionId: obj.attributeDefinitionId ?? null,
      staticPropertyName: obj.staticPropertyName ?? '',
      searchValue: obj.searchValue ?? '',
    };
    this.createFilterCards();
    this.applyFilter();
  }

  openFilterWindow() { this.isFilterWindowOpened = true; }
  closeFilterWindow() { this.isFilterWindowOpened = false; }

  onAddFilter() {
    this.createFilterCards();
    this.applyFilter();
  }

  applyFilter() {
    this.currentPage = 1;
    this.cardsFilter = this.buildRequest();
    this.loadList();
  }

  resetFilter() {
    this.filter = new DynamicCustomerFilter();
    this.filter.utilityId = this.utilityId;
    this.filter.filterParams.showAllNewEntries = !this.permissionsService.isCustomerNewEntryBreakdownAllowed;
    this.checklistItems = [];
    this.applyFilter();
  }

  removeFilter(item: ChecklistItem) {
    const fp = this.filter.filterParams;
    if (item.property.startsWith('attr:')) {
      const attrId = Number(item.property.split(':')[1]);
      const df = this.filter.dynamicFilters.find(f => f.attributeDefinitionId === attrId);
      if (df) {
        const option = this.getAttributeDefinition(attrId)?.optionDefinitions.find(o => o.value === item.selectedValue);
        if (option) df.selectedOptions = df.selectedOptions.filter(id => id !== option.id);
        else { df.from = null; df.to = null; }
        if (df.selectedOptions.length === 0 && !df.from && !df.to) {
          this.filter.dynamicFilters = this.filter.dynamicFilters.filter(f => f !== df);
        }
      }
    }
    switch (item.property) {
      case 'Search':
        this.filter.searchObject = { attributeDefinitionId: null, staticPropertyName: '', searchValue: '' };
        break;
      case 'Date From':
        fp.dateFrom = null;
        break;
      case 'Date To':
        fp.dateTo = null;
        break;
      case 'Validation Status':
        fp.selectedStatuses = fp.selectedStatuses.filter(s => this.validationStatusLabel(s) !== item.valueTranslation);
        break;
      case 'Review Status':
        fp.reviewStatus = null;
        break;
      case 'Audit Status':
        fp.auditStatus = null;
        break;
      case 'Tag':
        fp.tag = null;
        break;
      case 'Tariff':
        fp.tariffId = null;
        break;
      case 'SLT Status':
        fp.sltStatus = null;
        break;
      case 'Use of Premises':
        fp.useOfPremises = null;
        break;
      case 'Direct Connection':
        fp.showDirectConnection = false;
        break;
      case 'Region':
        fp.selectedRegionModel = null;
        fp.selectedRegions = [];
        fp.selectedAreaModels = [];
        fp.selectedAreas = [];
        break;
      case 'District':
        fp.selectedAreaModels = fp.selectedAreaModels.filter(a => a.name !== item.selectedValue);
        fp.selectedAreas = fp.selectedAreaModels.map(a => a.id);
        break;
      case 'Geographical Region':
        fp.selectedGeoRegionModel = null;
        fp.selectedGeographicalRegions = [];
        fp.selectedSubGeoModels = [];
        fp.selectedSubGeographicalRegions = [];
        break;
      case 'Sub-Geographical Region':
        fp.selectedSubGeoModels = fp.selectedSubGeoModels.filter(s => s.name !== item.selectedValue);
        fp.selectedSubGeographicalRegions = fp.selectedSubGeoModels.map(s => s.id);
        break;
      case 'User':
        fp.selectedUserModels = fp.selectedUserModels.filter(u => u.name !== item.selectedValue);
        fp.selectedUsers = fp.selectedUserModels.map(u => u.id);
        break;
    }
    this.createFilterCards();
    this.applyFilter();
  }

  private createFilterCards() {
    const items: ChecklistItem[] = [];
    const fp = this.filter.filterParams;

    if (this.filter.searchObject.searchValue) {
      items.push(new ChecklistItem('Search', this.filter.searchObject.searchValue, 'COMMON.SEARCH', this.filter.searchObject.searchValue));
    }
    if (fp.dateFrom) items.push(new ChecklistItem('Date From', fp.dateFrom, 'COMMON.DATE_FROM', fp.dateFrom));
    if (fp.dateTo) items.push(new ChecklistItem('Date To', fp.dateTo, 'COMMON.DATE_TO', fp.dateTo));
    fp.selectedStatuses.forEach(s => {
      const label = this.validationStatusLabel(s);
      items.push(new ChecklistItem('Validation Status', label, 'COMMON.VALIDATION_STATUS', label));
    });
    if (fp.reviewStatus != null) {
      const label = fp.reviewStatus ? 'COMMON.REVIEW_STATUS_OPTIONS.REVIEWED' : 'COMMON.REVIEW_STATUS_OPTIONS.NOT_REVIEWED';
      items.push(new ChecklistItem('Review Status', label, 'COMMON.REVIEW_STATUS', label));
    }
    if (fp.auditStatus != null) {
      const label = fp.auditStatus ? 'COMMON.ASSET_STATUS.AUDITED' : 'COMMON.ASSET_STATUS.NON_AUDITED';
      items.push(new ChecklistItem('Audit Status', label, 'COMMON.AUDIT_STATUS', label));
    }
    if (fp.tag != null) {
      const label = fp.tag ? 'COMMON.TAG.NEW_ENTRY' : 'COMMON.TAG.EXISTING';
      items.push(new ChecklistItem('Tag', label, 'COMMON.TAG.TITLE', label));
    }
    if (fp.tariffId != null) {
      const name = this.tariffs.find(t => t.id == fp.tariffId)?.name ?? fp.tariffId.toString();
      items.push(new ChecklistItem('Tariff', name, 'COMMON.TARIFF.SINGLE', name));
    }
    if (fp.sltStatus != null) {
      const name = this.sltOptions.find(s => s.key == fp.sltStatus)?.value ?? fp.sltStatus.toString();
      items.push(new ChecklistItem('SLT Status', name, 'COMMON.SLT_STATUS.SINGLE', name));
    }
    if (fp.useOfPremises != null) {
      const label = CUSTOMER_USE_OF_PREMISES.find(u => u.value == fp.useOfPremises)?.translation ?? fp.useOfPremises.toString();
      items.push(new ChecklistItem('Use of Premises', label, 'COMMON.USE_OF_PREMISES', label));
    }
    if (fp.showDirectConnection) {
      items.push(new ChecklistItem('Direct Connection', 'CUSTOMERS.DIRECT', 'CUSTOMERS.DIRECT', 'CUSTOMERS.DIRECT'));
    }
    if (fp.selectedRegionModel) {
      items.push(new ChecklistItem('Region', fp.selectedRegionModel.name, 'COMMON.BU.SINGLE', fp.selectedRegionModel.name));
    }
    fp.selectedAreaModels.forEach(a => items.push(new ChecklistItem('District', a.name, 'COMMON.UT.SINGLE', a.name)));
    if (fp.selectedGeoRegionModel) {
      items.push(new ChecklistItem('Geographical Region', fp.selectedGeoRegionModel.name, 'COMMON.BU.SINGLE', fp.selectedGeoRegionModel.name));
    }
    fp.selectedSubGeoModels.forEach(s => items.push(new ChecklistItem('Sub-Geographical Region', s.name, 'COMMON.UT.SINGLE', s.name)));
    fp.selectedUserModels.forEach(u => items.push(new ChecklistItem('User', u.name, 'COMMON.USER.SINGLE', u.name)));

    this.filter.dynamicFilters.forEach(df => {
      const attr = this.getAttributeDefinition(df.attributeDefinitionId!);
      if (!attr) return;
      const property = `attr:${attr.id}`;
      df.selectedOptions.forEach(optionId => {
        const value = attr.optionDefinitions.find(o => o.id === optionId)?.value ?? optionId.toString();
        items.push(new ChecklistItem(property, value, attr.label, value));
      });
      if (df.from || df.to) {
        const range = `${df.from ? new Date(df.from).toLocaleDateString() : ''} - ${df.to ? new Date(df.to).toLocaleDateString() : ''}`;
        items.push(new ChecklistItem(property, range, attr.label, range));
      }
    });

    this.checklistItems = items;
  }

  private getAttributeDefinition(attrId: number) {
    return this.filterEmc.find(x => x.attributeDefinitionId === attrId)?.attributeDefinition;
  }

  private validationStatusLabel(status: number): string {
    return CUSTOMER_VALIDATION_STATUSES.find(s => s.value === status)?.translation ?? status.toString();
  }

  downloadCustomerInfo() {
    this.showDownload = false;
    const request = this.buildRequest();
    request.pageInfo = { page: -1, pageSize: -1 };
    this.customerService.downloadFileDynamic(request).subscribe({
      next: (response) => this.handleDownloadResponse(response.body),
      error: () => this.toastr.error('Request failed'),
    });
  }

  downloadReplacedSlrn() {
    this.showDownload = false;
    this.customerService.downloadReplacedSlrnDynamic(this.buildRequest()).subscribe({
      next: (response) => this.handleDownloadResponse(response.body),
      error: () => this.toastr.error('Request failed'),
    });
  }

  private handleDownloadResponse(data: any) {
    if (data?.status === 'OK' || data?.status === '200') window.location.href = data.data;
    else if (data?.status === 'Pending' || data?.status === '600') this.toastr.info(data?.message || '');
    else this.toastr.error(data?.message || 'Request failed');
  }

  onRowClick(guid: string) {
    if (!guid) return;
    localStorage.setItem(this.storageKey, JSON.stringify(this.filter));
    localStorage.setItem('cameFrom', 'Customer');
    this.router.navigate([`/customers/edit/${guid}`]);
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.showDownload = false;
  }

  private buildVisibleColumns() {
    this.visibleColumns = this.listPageEmc
      .map((emc, index) => ({ emc, index }))
      .filter(({ emc }) => this.isColumnVisible(emc.staticPropertyName))
      .map(({ emc, index }) => ({ label: emc.labelName, index }));
  }

  private isColumnVisible(staticName: string | null): boolean {
    switch (staticName) {
      case 'ElectricalAddressCode':
        return this.permissionsService.isCustomerElectricalAddressCodeAllowed;
      case 'PropertyCode':
        return this.permissionsService.isCustomerPropertyCodeAllowed;
      case 'ValidationStatus':
        return this.permissionsService.isValidationStatusAllowed;
      case 'ReviewStatus':
        return this.permissionsService.isReviewStatusAllowed;
      case 'AuditStatus':
        return this.permissionsService.isCustomerAuditReviewAllowed;
      default:
        return true;
    }
  }
}
